import { ModifierProperty } from '@/enums';
import { useGetComponentConfigByModule } from '@/hooks';
import {
  CustomAutoComplete,
  CustomCheckbox,
  CustomCheckboxGroup,
  CustomDatePicker,
  CustomInput,
  CustomRadioGroup,
  CustomSelect,
  CustomSwitcher,
} from '@amili-v3/amili-v3-design';
import CustomTextAreaInput from '@amili-v3/amili-v3-design/src/components/Input/CustomTextArea';
import CustomNumberInput from '@amili-v3/amili-v3-design/src/components/Input/NumberInput';
import PasswordInput from '@amili-v3/amili-v3-design/src/components/Input/PasswordInput';
import CustomTimePicker from '@amili-v3/amili-v3-design/src/components/TimePicker';
import { memo } from 'react';
import { transformOptionsByConfig } from './utils';
import { ComponentMap, DictionaryInputProps, DictionaryKey } from '@/types';

const componentMap: ComponentMap = {
  select: CustomSelect,
  checkbox: CustomCheckbox,
  checkboxGroup: CustomCheckboxGroup,
  input: CustomInput,
  password: PasswordInput,
  number: CustomNumberInput,
  switch: CustomSwitcher,
  radioGroup: CustomRadioGroup,
  datePicker: CustomDatePicker,
  timePicker: CustomTimePicker,
  'text-area': CustomTextAreaInput,
  'auto-complete': CustomAutoComplete,
};

type InputConfigProps<P extends DictionaryKey> = DictionaryInputProps[P] & {
  id: string;
  inputType: P;
};

const CustomInputConfig = <T extends DictionaryKey>(
  props: InputConfigProps<T>,
) => {
  const { inputType, id, disabled: originDisabled, ...rest } = props;
  const customKey = `${id}-${inputType}`;

  const inputConfig = useGetComponentConfigByModule('input');
  const hidden = inputConfig[customKey]?.modifier === ModifierProperty.HIDDEN;
  const disabled =
    inputConfig[customKey]?.modifier === ModifierProperty.DISABLED;

  const Input = componentMap[inputType];

  const newOptions = transformOptionsByConfig({
    inputType,
    customKey,
    dataConfig: inputConfig,
    options: (props as any).options,
    isPreventCustomConfig: false,
  });

  if (hidden) return null;
  return (
    <Input
      {...(rest as any)}
      id={id}
      data-customization-key={customKey}
      disabled={disabled || originDisabled}
      options={newOptions}
    />
  );
};

export default memo(CustomInputConfig) as typeof CustomInputConfig;
